import request from ".";
import { CommonListParams } from "@/types/common";
import { getStorage } from "@/utils/storage";
import {
  BorrowParams,
  Equipment,
  EquipmentCategory,
  EquipmentCategoryParams,
  EquipmentEnumItem,
  EquipmentListParams,
  EquipmentQrCode,
  EquipmentRecord,
  EquipmentRecordParams,
  EquipmentSaveParams,
  MaintainScan,
  MaintainUpcoming,
  ReturnParams,
  ScrapParams,
} from "@/types/equipment";

const URL = "/wms/v2/equipment";

const apis = {
  addCategory: `${URL}/category/add`,
  addSubCategory: (id: string) => `${URL}/category/${id}/subs/add`,
  updateCategory: (id: string) => `${URL}/category/${id}/update`,
  disableCategory: (id: string) => `${URL}/category/${id}/disable`,
  enableCategory: (id: string) => `${URL}/category/${id}/enable`,
  fetchCategory: (id: string) => `${URL}/category/${id}/fetch`,
  listCategories: `${URL}/category/list`,
  categoryTree: `${URL}/category/tree`,
  statuses: `${URL}/enum/statuses`,
  recordTypes: `${URL}/enum/recordTypes`,
  thresholdUnits: `${URL}/enum/thresholdUnits`,
  save: `${URL}/save`,
  fetch: (id: string) => `${URL}/${id}/fetch`,
  list: `${URL}/list`,
  disable: (id: string) => `${URL}/${id}/disable`,
  enable: (id: string) => `${URL}/${id}/enable`,
  qrCode: (id: string) => `${URL}/${id}/qrCode`,
  addRecord: (id: string) => `${URL}/${id}/record/add`,
  records: (id: string) => `${URL}/${id}/records`,
  fetchRecord: (id: string) => `${URL}/record/${id}/fetch`,
  borrow: (id: string) => `${URL}/${id}/borrow`,
  doReturn: (id: string) => `${URL}/${id}/return`,
  scrap: (id: string) => `${URL}/${id}/scrap`,
  myBorrowed: `${URL}/myBorrowed`,
  upcomingMaintain: `${URL}/maintain/upcoming`,
  scanMaintain: `${URL}/maintain/scan`,
  updatePlaceLocation: (id: string) => `${URL}/${id}/placeLocation/update`,
};

// 新建根类目
export const addCategory = (body: EquipmentCategoryParams) =>
  request<EquipmentCategory, EquipmentCategoryParams>(apis.addCategory, {
    params: { body },
  }).then(({ body: result }) => result);

// 新建子类目
export const addSubCategory = (id: string, body: EquipmentCategoryParams) =>
  request<EquipmentCategory, EquipmentCategoryParams>(apis.addSubCategory(id), {
    params: { body },
  }).then(({ body: result }) => result);

// 修改类目
export const updateCategory = (id: string, body: EquipmentCategoryParams) =>
  request<EquipmentCategory, EquipmentCategoryParams>(apis.updateCategory(id), {
    params: { body },
  }).then(({ body: result }) => result);

// 禁用类目（级联子类目）
export const disableCategory = (id: string) =>
  request<void>(apis.disableCategory(id)).then(() => undefined);

// 启用类目（级联父类目）
export const enableCategory = (id: string) =>
  request<void>(apis.enableCategory(id)).then(() => undefined);

// 获取类目详情
export const fetchCategory = (id: string, controller?: AbortController) =>
  request<EquipmentCategory>(apis.fetchCategory(id), { controller })
    .then(({ body }) => body);

// 获取类目平铺列表
export const listCategories = (
  params?: CommonListParams<string | null>,
) =>
  request<EquipmentCategory[], string | null>(apis.listCategories, { params })
    .then(({ body, pagination }) => ({ body, pagination }));

// 获取类目树
export const categoryTree = (controller?: AbortController) =>
  request<EquipmentCategory[]>(apis.categoryTree, { controller })
    .then(({ body }) => body);

// 获取设备状态枚举
export const statuses = (controller?: AbortController) =>
  request<EquipmentEnumItem[]>(apis.statuses, { controller }).then(({ body }) => body);

// 获取记录类型枚举
export const recordTypes = (controller?: AbortController) =>
  request<EquipmentEnumItem[]>(apis.recordTypes, { controller })
    .then(({ body }) => body);

// 获取使用量单位枚举
export const thresholdUnits = () =>
  request<EquipmentEnumItem[]>(apis.thresholdUnits).then(({ body }) => body);

// 新建或编辑设备（必填 keeperOrgId、categoryId）
export const save = (body: EquipmentSaveParams) =>
  request<Equipment, EquipmentSaveParams>(apis.save, {
    params: { body },
  }).then(({ body: result }) => result);

// 获取设备详情
export const fetch = (id: string, controller?: AbortController) =>
  request<Equipment>(apis.fetch(id), { controller }).then(({ body }) => body);

type EquipmentListRequestParams =
  Omit<CommonListParams<EquipmentListParams>, "body"> & {
    body: EquipmentListParams;
  };

// 获取当前类目设备列表（默认按 keeperOrgId 树形向下可见）
export const list = (params: EquipmentListRequestParams) =>
  request<Equipment[], EquipmentListParams>(apis.list, {
    params,
    requestConfig: { headers: { org_id: getStorage("curOrgId") } },
  })
    .then(({ body, pagination }) => ({ body, pagination }));

// 软禁用设备
export const disable = (id: string) =>
  request<void>(apis.disable(id)).then(() => undefined);

// 启用设备
export const enable = (id: string) =>
  request<void>(apis.enable(id)).then(() => undefined);

// 生成或获取设备二维码
export const qrCode = (id: string, controller?: AbortController) =>
  request<EquipmentQrCode>(apis.qrCode(id), { controller })
    .then(({ body }) => body);

// 添加维护记录（保养、维修、加油、充电或检查）
export const addRecord = (id: string, body: EquipmentRecordParams) =>
  request<EquipmentRecord, EquipmentRecordParams>(apis.addRecord(id), {
    params: { body },
  }).then(({ body: result }) => result);

// 获取设备记录流水（支持按记录类型过滤）
export const records = (
  id: string,
  recordType?: number | null,
  controller?: AbortController,
) =>
  request<EquipmentRecord[], number | null>(apis.records(id), {
    params: { body: recordType },
    controller,
  }).then(({ body }) => ({ body }));

// 获取单条设备记录详情
export const fetchRecord = (id: string, controller?: AbortController) =>
  request<EquipmentRecord>(apis.fetchRecord(id), { controller })
    .then(({ body }) => body);

// 领用设备
export const borrow = (id: string, body: BorrowParams) =>
  request<EquipmentRecord, BorrowParams>(apis.borrow(id), {
    params: { body },
  }).then(({ body: result }) => result);

// 归还设备
export const doReturn = (id: string, body: ReturnParams) =>
  request<EquipmentRecord, ReturnParams>(apis.doReturn(id), {
    params: { body },
  }).then(({ body: result }) => result);

// 报废设备（不可逆）
export const scrap = (id: string, body: ScrapParams) =>
  request<EquipmentRecord, ScrapParams>(apis.scrap(id), {
    params: { body },
  }).then(({ body: result }) => result);

// 获取我领用的设备列表
export const myBorrowed = (
  params?: Pick<CommonListParams<never>, "pagination">,
  controller?: AbortController,
) =>
  request<Equipment[], never>(apis.myBorrowed, { params, controller })
    .then(({ body, pagination }) => ({ body, pagination }));

// 查询保养到期设备（天数和使用量双维度）
export const upcomingMaintain = (controller?: AbortController) =>
  request<MaintainUpcoming[]>(apis.upcomingMaintain, {
    controller,
    requestConfig: { headers: { org_id: getStorage("curOrgId") } },
  })
    .then(({ body }) => ({
      // 保留列表响应结构，并以设备 ID 适配 useDataList 的行标识。
      body: (body ?? []).map(item => ({ ...item, id: item.equipment.id })),
    }));

// Scheduler 保养扫描入口
export const scanMaintain = () =>
  request<MaintainScan>(apis.scanMaintain).then(({ body }) => body);

// 单独修改设备放置地点（null 表示清空）
export const updatePlaceLocation = (
  id: string,
  body: string | null,
) =>
  request<Equipment, string | null>(apis.updatePlaceLocation(id), {
    params: { body },
  }).then(({ body: result }) => result);
